import React, { useEffect, useState, useMemo, useRef } from 'react'
import { FaDoorOpen, FaFileAlt, FaTools } from 'react-icons/fa';
import { MdOutlineViewInAr } from 'react-icons/md';
import { GiPlatform } from 'react-icons/gi';
import FormGeneralLocker from './FormGeneralLocker';
import PreviewLocker from './PreviewLocker/index';
import FormSettingsLocker from './FormSettingsLocker';
import FormModulesLocker from './FormModulesLocker';
import FormDoorsLocker from './FormDoorsLocker';
import "./index.css";
import { usePlatforms } from '../../../../hooks/usePlatforms';
import { translate } from '../../../../utils/Common';
import { v4 as uuidv4 } from 'uuid';
import ModalContent from '../../../../components/ModalContent';
import ContentCard from '../../../../components/layout/ContentCard';
import Tabs from '../../../../components/common/Tabs';
import styles from './PlatformSettingsForm.module.css';
import { compareDoorPosition } from './doorOrder';

const CONFIG_KEYS = ['general', 'settings', 'modules', 'doors'];

const configSelector = {
    general: {
        title: translate("sidebar.options.platforms.general"),
        icon: <FaFileAlt size={16} />,
        components: (props) => <FormGeneralLocker {...props} />,
    },
    settings: {
        title: translate("global.configuration"),
        icon: <FaTools size={16} />,
        components: (props) => <FormSettingsLocker {...props} />,
    },
    modules: {
        title: translate("global.modules"),
        icon: <GiPlatform size={18} />,
        components: (props) => <FormModulesLocker {...props} />,
    },
    doors: {
        title: translate("global.Doors"),
        icon: <FaDoorOpen size={16} />,
        components: (props) => <FormDoorsLocker {...props} />,
    }
};

const GroupFormCreate = ({setModulesIsCompleted, settingsLocker, setSettingsLocker, permissions }) => {
     const { getPlatformDetailsByExternalId, getDoorsByPlatform, getModulesByPlatform, getProviderPlatforms, updateDoorPlatform } = usePlatforms();
     const [platformDetails, setPlatformDetails] = useState(null);          

    const queryParams = new URLSearchParams(window.location.search);
    const extIdParams = queryParams.get('extId');
    const typeView = queryParams.get('view');
    const optParams = queryParams.get('opt') || 'general';
    
    const [optionSelect, setOptionSelect] = useState(optParams);
    const [selectDoor, setSelectDoor] = useState(null);
    const [externalId, setExternalId] = useState(extIdParams);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingDoor, setIsLoadingDoor] = useState(false);
    const [configLocker, setConfigLocker] = useState({
        modules: [],
        settings: {},
    });
     const [optionsProviders, setOptionsProviders] = useState([]);
    const [doorsInBd, setDoorsInBd] = useState([]);
    const [editModeModule, setEditModeModule] = useState(false);  
    const [editModeDoors, setEditModeDoors] = useState(false);
    const [refreshAssignments, setRefreshAssignments] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const [modal, setModal] = useState({ title: '', body: '', footer: '' });
    // Estado para rastrear cambios pendientes de orden de puertas
    const [pendingDoorOrderChanges, setPendingDoorOrderChanges] = useState({});
    // Ref para hacer scroll a la sección del formulario (info de la puerta) en pantallas pequeñas
    const leftColumnRef = useRef(null);

    const handleOptionChange = (value) => {
        setOptionSelect(value);
        if (value) {
            // Agregar el parámetro extId en la URL
           const url = new URL(window.location.href);
           url.searchParams.set('opt', value);
           window.history.pushState({}, '', url); 
        }
           
    }
    
    const handleGetInfoPlatform = async () => {
        setIsLoading(true);
        const abortController = new AbortController();
        if (extIdParams) {
            
            const response = await getPlatformDetailsByExternalId(extIdParams, abortController.signal);
            
            if (!response) {
                // Mostrar modal cuando no se encuentra la plataforma
                setModal({
                    title: translate("global.alert.attention"),
                    body: translate("dashboard.platforms.notFound"),
                    footer: (
                        <button 
                            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/90" 
                            onClick={() => {
                                setShowModal(false);
                                window.location.href = '/dashboard/platforms/general';
                            }}
                        >
                            {translate('global.return')}
                        </button>
                    ),
                });
                setShowModal(true);
                setIsLoading(false);
                return;
            } else {
                const url = new URL(window.location.href);
                url.searchParams.set('view', 'edit');
                window.history.pushState({}, '', url); 
            }

            setExternalId(response?.externalId);
            setPlatformDetails(response);
            setConfigLocker({
                ...configLocker,
                settings: response || {},
            });
            setSettingsLocker(response);
            // Mientras se cargan módulos y puertas, mantener los estados de carga activos
            setIsLoadingDoor(true);
            await handleGetDoors();
            setIsLoadingDoor(false);
            setIsLoading(false);
        }
    };    

    const replaceDoors = async (modules, doorsFromDB) => {
        // Iteramos sobre cada módulo generado
        modules.forEach(module => {
            // Filtramos las puertas de la base de datos que pertenecen a este módulo
            const doorsForModule = doorsFromDB.filter(door => door.moduleId === module.moduleId);

            // Caso B: módulo sin puertas reales => mantener distribución original equilibrada
            if (!doorsForModule.length) {
                // Limpiar columnas
                module.columns.forEach(column => {
                    column.doors = [];
                });

                const startDoorNumber = typeof module.startDoorNumber === "number"
                    ? module.startDoorNumber
                    : Number(module.startDoorNumber) || 1;

                const totalDoors = module.numDoors || 20;
                const numColumns = module.numColumns || 2;
                const doorsPerColumn = Math.floor(totalDoors / numColumns);
                const remainder = totalDoors % numColumns;

                let doorCounter = startDoorNumber;
                let globalSequence = 1;

                for (let i = 0; i < numColumns; i++) {
                    const column = module.columns.find(col => col.columnId === i + 1);
                    if (!column) continue;

                    const doorsInThisColumn = doorsPerColumn + (i >= numColumns - remainder ? 1 : 0);
                    for (let j = 0; j < doorsInThisColumn; j++) {
                        column.doors.push({
                            doorNumber: doorCounter++,
                            doorSequence: globalSequence++,
                            dimension: module.dimension || "S",
                            layer: module.layer || "H",
                            isScreen: module.isScreen || false,
                            isActive: true,
                            locationId: module.locationId,
                            moduleId: module.moduleId,
                            columnModule: column.columnId,
                            moduleNumber: module.moduleNumber,
                            id: uuidv4(),
                            boxitDoorId: 0,
                            comPort: ""
                        });
                    }
                }

                return;
            }

            // Caso A: módulo con puertas reales => respetar puertas y crear borradores solo en última columna

            // Limpiar todas las columnas del módulo
            module.columns.forEach(column => {
                column.doors = [];
            });

            // Obtener el número inicial del módulo
            const startDoorNumber = typeof module.startDoorNumber === "number"
                ? module.startDoorNumber
                : Number(module.startDoorNumber) || 1;

            // Columna donde se deben crear las nuevas puertas borrador (última columna)
            const lastColumnId = module.columns.reduce(
                (max, col) => (col.columnId > max ? col.columnId : max),
                1
            );
            const lastColumn = module.columns.find(col => col.columnId === lastColumnId);

            // 1) Colocar todas las puertas reales en su columna correcta
            let maxExistingSequence = 0;
            doorsForModule.forEach(dbDoor => {
                if (!dbDoor.doorSequence) return;

                const targetColumn = module.columns.find(col => col.columnId === dbDoor.columnModule) || lastColumn;
                if (!targetColumn) return;

                maxExistingSequence = Math.max(maxExistingSequence, dbDoor.doorSequence);

                targetColumn.doors.push({
                    id: uuidv4(),
                    boxitDoorId: dbDoor.boxitDoorId,
                    doorNumber: dbDoor.doorNumber,
                    doorSequence: dbDoor.doorSequence,
                    dimension: dbDoor.dimension,
                    layer: dbDoor.layer,
                    isScreen: dbDoor.isScreen,
                    isActive: dbDoor.isActive,
                    locationId: dbDoor.locationId || module.locationId,
                    moduleId: dbDoor.moduleId || module.moduleId,
                    columnModule: dbDoor.columnModule || lastColumnId,
                    moduleNumber: module.moduleNumber,
                    comPort: dbDoor.comPort || "",
                    doorPort: dbDoor.doorPort,
                });
            });

            // 2) Ordenar las columnas por doorSequence
            module.columns.forEach(column => {
                column.doors.sort((a, b) => (a.doorSequence || 0) - (b.doorSequence || 0));
            });

            const totalDoors = module.numDoors || 20;

            // 3) Crear nuevas puertas borrador SOLO en la última columna,
            // siguiendo la secuencia después de la última puerta existente.
            // Aquí contamos puertas reales del módulo; si hay menos que totalDoors,
            // generamos borradores adicionales aunque la secuencia global sea mayor
            // que totalDoors (caso módulos 2, 3, ... donde las secuencias siguen).
            if (lastColumn) {
                const existingCount = doorsForModule.length;
                const draftsToCreate = totalDoors - existingCount;

                if (draftsToCreate > 0) {
                    let nextSequence = Math.max(maxExistingSequence, 0) + 1;

                    const pushDraft = (column) => {
                        column.doors.push({
                            doorNumber: startDoorNumber + nextSequence - 1,
                            doorSequence: nextSequence,
                            dimension: module.dimension || "S",
                            layer: module.layer || "H",
                            isScreen: module.isScreen || false,
                            isActive: true,
                            locationId: module.locationId,
                            moduleId: module.moduleId,
                            columnModule: column.columnId,
                            moduleNumber: module.moduleNumber,
                            id: uuidv4(),
                            boxitDoorId: 0,
                            comPort: ""
                        });
                        nextSequence++;
                    };

                    // Una columna que se quedó sin puertas (p. ej. se eliminaron todas las suyas)
                    // recibe primero un borrador, para no dibujarla vacía y poder crear ahí.
                    let remaining = draftsToCreate;
                    module.columns
                        .slice()
                        .sort((a, b) => a.columnId - b.columnId)
                        .forEach(column => {
                            if (remaining > 0 && column !== lastColumn && column.doors.length === 0) {
                                pushDraft(column);
                                remaining--;
                            }
                        });

                    // El resto, como siempre, al final de la última columna
                    for (let i = 0; i < remaining; i++) pushDraft(lastColumn);
                }

                // Ordenar nuevamente la última columna por secuencia
                lastColumn.doors.sort((a, b) => (a.doorSequence || 0) - (b.doorSequence || 0));
            }
        });

        return modules;
    }

    const reorganizeAndFixDoors = async (moduleCompleteGenerate, doorsFromDB) => {
        // Reorganizar las puertas en los módulos
        const reorganizedModules = await reorganizeDoors(moduleCompleteGenerate, doorsFromDB);
    
        // Corregir las puertas dentro de cada módulo
        for (const module of reorganizedModules) {
            fixDoorsInModule(module);
        }
    
        return reorganizedModules;
    };
    

    const reorganizeDoors = async (moduleCompleteGenerate, doorsFromDB) => {
        for (const door of doorsFromDB) {
            const { moduleId, columnModule, doorSequence } = door;
    
            // Encuentra el módulo correspondiente
            const module = moduleCompleteGenerate.find(mod => mod.moduleId === moduleId);
            if (!module) continue;
    
            // Encuentra la columna correspondiente
            const column = module.columns.find(col => col.columnId === columnModule);
            if (column) {
                // Verifica si la puerta ya está en la columna
                const existingDoor = column.doors.find(d => d.doorSequence === doorSequence);
                if (!existingDoor) {
                    column.doors.push(door); // Agrega la puerta si no existe
                } else {
                    // Puedes manejar casos específicos aquí si es necesario
                }
    
                // Ordena las puertas de la columna por `doorSequence` (menor a mayor)
                column.doors.sort((a, b) => a.doorSequence - b.doorSequence);
            }
    
            // Verifica si la puerta está en la columna y posición correctas
            const existingDoorIndex = column.doors.findIndex(d => d.doorSequence === doorSequence);
            if (existingDoorIndex === -1) {
                // Si no está en la posición correcta, mover la puerta
                removeDoorFromOtherColumns(module.columns, door.id); // Función para eliminar la puerta de otras columnas
                column.doors.push(door); // Agregar la puerta a la columna correcta
                column.doors.sort((a, b) => a.doorSequence - b.doorSequence); // Asegura el orden nuevamente
            }
        }
    
        // Validar que no haya más puertas que `numDoors` por módulo
        for (const module of moduleCompleteGenerate) {
            const totalDoors = module.columns.reduce((sum, col) => sum + col.doors.length, 0);
            if (totalDoors > module.numDoors) {
                // console.error(`Exceso de puertas en el módulo ${module.moduleNumber}`);
            }
        }
    
        // Retornar los módulos reorganizados
        return moduleCompleteGenerate;
    };    

          // Función para corregir las puertas en el módulo
    const fixDoorsInModule = (module) => {
            
            if (!module || !module.columns) {
                return;
            }
        
            // Recorremos cada columna
            module.columns.forEach(column => {
                // Filtramos las puertas que no corresponden al columnModule
                column.doors = column.doors.filter(door => {
                    const isCorrectColumn = door.columnModule === column.columnId;
                    if (!isCorrectColumn) {
                    }
                    return isCorrectColumn;
                });
            });
            
        }
    

    const removeDoorFromOtherColumns =(columns, doorId) => {
        for (const column of columns) {
            column.doors = column.doors.filter(door => door.id !== doorId);
        }
    }

    const handleGetDoors = async () => {
        // 1. Obtén los módulos primero.
        const responseModule = await handleGetModules();

        // 2. Obtenemos las puertas desde la base de datos (orden por doorOrder/doorSequence).
        const abortController = new AbortController();
        const doorsInBd = await getDoorsByPlatform(externalId, abortController.signal);

        setDoorsInBd(doorsInBd);

        const updateModules = await replaceDoors(responseModule.modules, doorsInBd);

        // Ordenar cada columna por doorSequence (alineado con doorOrder en BD).
        updateModules.forEach((module) => {
            if (module.columns) {
                module.columns.forEach((column) => {
                    column.doors.sort((a, b) => (a.doorSequence || 0) - (b.doorSequence || 0));
                });
            }
        });

        setConfigLocker({ ...responseModule, modules: updateModules });
    };    
    
    const groupModules = async (modules) => {
        // Verificar si modules es inválido o está vacío
        if (!modules || modules.length === 0) return [];
    
        // Ordenar los módulos por `moduleNumber` antes de procesarlos
        modules.sort((a, b) => a.moduleNumber - b.moduleNumber);
    
        const groupedModules = [];
        let sequenceCounter = 1; // Contador para numeración continua
    
        modules.forEach((module) => {
            let existingModule = groupedModules.find(m => m.moduleNumber === module.moduleNumber);
    
            if (!existingModule) {
                existingModule = {
                    ...module,
                    status: 3,
                    columns: [],
                    isUnitCentral: !!module.isUnitCentral,
                    isRefrigerated: !!module.isRefrigerated,
                    moduleId: module.moduleId[0],
                    startDoorNumber: null, // Secuencia inicial del módulo
                    endDoorNumber: null    // Secuencia final del módulo
                };
                groupedModules.push(existingModule);
            }
    
            const totalDoors = parseInt(module.numDoors, 10);
            const numColumns = parseInt(module.numColumns, 10);
    
            // Inicializar contador de secuencia para puertas del módulo actual
            let moduleDoorSequence = 1;
            let moduleStartSequence = sequenceCounter; // Guardar el inicio de la secuencia
    
            // Distribuir las puertas equitativamente entre las columnas,
            // asignando los "sobrantes" (remainder) a las últimas columnas
            // para que nuevos borradores se generen en la última columna.
            const doorsPerColumn = Math.floor(totalDoors / numColumns);
            const remainder = totalDoors % numColumns;
    
            for (let i = 0; i < numColumns; i++) {
                const doorsInThisColumn = doorsPerColumn + (i >= numColumns - remainder ? 1 : 0);
                const column = {
                    columnId: i + 1,
                    doors: []
                };
    
                for (let j = 0; j < doorsInThisColumn; j++) {
                    column.doors.push({
                        doorNumber: sequenceCounter++, // Asignar números de puerta de forma continua
                        dimension: module.dimension || "S",
                        layer: module.layer || "H",
                        isScreen: module.isScreen || false,
                        locationId: module.locationId,
                        moduleId: module.moduleId[0],
                        columnModule: i + 1,
                        doorSequence: moduleDoorSequence++,
                        moduleNumber: module.moduleNumber,
                        isActive: true,
                        id: uuidv4(),
                        boxitDoorId: 0,
                        comPort: ""
                    });
                }
    
                // Ordenar las puertas de la columna por `doorSequence`
                column.doors.sort((a, b) => a.doorSequence - b.doorSequence);
    
                existingModule.columns.push(column);
            }
    
            // Asignar los valores inicial y final de la secuencia al módulo
            existingModule.startDoorNumber = moduleStartSequence;
            existingModule.endDoorNumber = sequenceCounter - 1;
        });
    
        return groupedModules;
    };
    
      const handleGetModules = async () => {
        const abortController = new AbortController();
        const response = await getModulesByPlatform(externalId, abortController.signal);
        
        const grouped = await groupModules(response);  
          
        const result = {...configLocker, modules: grouped};
        return result;
      };


    const groupDoors = async (doorArray) => {
        if (doorArray?.length === 0) return [];
        const groupedDoors = {};
        doorArray?.forEach(door => {
            const key = `${door.boxitDoorId}`;
            if (!groupedDoors[key]) {
                groupedDoors[key] = {
                    locationId: door.locationId,
                    moduleId: door.moduleId,
                    boxitLocationId: door.boxitLocationId,
                    boxitModuleId: door.boxitModuleId,
                    boxitDoorId: door.boxitDoorId,
                    doorNumber: door.doorNumber,
                    dimension: door.dimension,
                    comPort: door.comPort,
                    createdAt: door.createdAt,
                    doorSequence: door.doorSequence,
                    isActive: door.isActive,
                    isAvailable: door.isAvailable,
                    isScreen: door.isScreen,
                    columnModule: door.columnModule,
                    layer: door.layer,
                    packageStatusId: door.packageStatusId,
                    moduleNumber: Number(door.moduleNumber),
                    packages: [],
                    isRefrigerated: door.isRefrigerated,
                    isUnitCentral: door.isUnitCentral
                };
            }
            if (groupedDoors[key].packages.length > 0 || door.packageId) {
                groupedDoors[key].packages.push({
                    packageId: door.packageId,
                    packageNumber: door.packageNumber,
                });
            }
        });
    
        const groupedDoorsArray = Object.values(groupedDoors);
    
        // Por moduleNumber y luego por doorSequence, igual que la vista del locker
        return groupedDoorsArray.sort(compareDoorPosition);
    };

      const getProvidersPlatforms = async () => {
        const response = await getProviderPlatforms();
        
        setOptionsProviders(response);
      }

      // Guarda los cambios pendientes de orden de puertas sin actualizar inmediatamente
      const handleDoorOrdersChange = (doorsChanged) => {
        if (!Array.isArray(doorsChanged) || doorsChanged.length === 0) return;

        // Guardar los cambios pendientes agrupados por módulo
        const changesByModule = {};
        doorsChanged.forEach(door => {
          if (!changesByModule[door.moduleId]) {
            changesByModule[door.moduleId] = [];
          }
          changesByModule[door.moduleId].push({
            ...door,
            oldSequence: door.oldSequence,
            newSequence: door.doorSequence,
          });
        });

        // Se acumulan por puerta: cada arrastre solo trae las que cambiaron respecto al anterior, así que
        // reemplazar la lista del módulo perdía las posiciones de un arrastre previo sin guardar.
        // Gana el último estado de cada puerta y se conserva su posición original.
        setPendingDoorOrderChanges(prev => {
          const updated = { ...prev };
          Object.entries(changesByModule).forEach(([moduleId, changes]) => {
            const byDoor = new Map((prev[moduleId] || []).map(door => [door.boxitDoorId, door]));
            changes.forEach(door => {
              const previous = byDoor.get(door.boxitDoorId);
              byDoor.set(door.boxitDoorId, previous ? { ...door, oldSequence: previous.oldSequence } : door);
            });
            updated[moduleId] = [...byDoor.values()];
          });
          return updated;
        });
      };

      // Vuelve a leer módulos y puertas (p. ej. después de eliminar una puerta)
      const reloadDoors = async () => {
        if (!externalId) return;
        setIsLoadingDoor(true);
        try {
          await handleGetDoors();
        } catch (error) {
          console.error('Error recargando puertas:', error);
        } finally {
          setIsLoadingDoor(false);
        }
      };

      // Descarta los cambios de orden pendientes de un módulo (cancelar, o sin permiso para reordenar).
      // Con reload vuelve a leer las puertas, para que el preview deje de mostrar el orden descartado.
      const discardPendingDoorOrderChanges = async (moduleId, { reload = false } = {}) => {
        if (!pendingDoorOrderChanges[moduleId]) return;
        setPendingDoorOrderChanges(prev => {
          const updated = { ...prev };
          delete updated[moduleId];
          return updated;
        });
        if (reload && externalId) {
          setIsLoadingDoor(true);
          try {
            await handleGetDoors();
          } catch (error) {
            console.error('Error recargando puertas después de descartar el orden:', error);
          } finally {
            setIsLoadingDoor(false);
          }
        }
      };

      // Actualiza en BD el doorOrder (doorSequence) de todas las puertas afectadas de forma ordenada
      const applyPendingDoorOrderChanges = async (moduleId) => {
        const moduleChanges = pendingDoorOrderChanges[moduleId];
        if (!moduleChanges || moduleChanges.length === 0) return { success: true };

        const abortController = new AbortController();
        let hasError = false;

        // Ordenar para evitar "The door already exists": primero las que se mueven a secuencia
        // más alta (liberan la posición), luego las que bajan. Así la posición destino queda
        // libre antes de asignarla.
        const sortedChanges = [...moduleChanges].sort((a, b) => b.newSequence - a.newSequence);

        // Actualizar cada puerta real que cambió de orden de forma secuencial
        for (const door of sortedChanges) {
          try {
            const dataToSend = {
              ...door,
              doorId: door.boxitDoorId,
              locationId: door.locationId,
              moduleId: door.moduleId,
              doorNumber: String(door.doorNumber),
              doorSequence: Number(door.doorSequence),
              doorPort: door.doorPort,
              dimension: door.dimension,
              layer: door.layer,
              isScreen: door.isScreen,
              columnNumber: Number(door.columnModule),
              isActive: door.isActive,
              isAvailable: door.isAvailable ?? true,
              comPort: door.comPort,
            };

            const response = await updateDoorPlatform(dataToSend, abortController.signal);
            if (response?.code !== 200) {
              hasError = true;
              console.error(`Error actualizando orden de puerta ${door.boxitDoorId}:`, response);
              break; // Detener si hay un error
            }
          } catch (e) {
            hasError = true;
            console.error(`Error actualizando orden de puerta ${door.boxitDoorId}:`, e);
            break; // Detener si hay un error
          }
        }

        // Si todas las actualizaciones fueron exitosas, limpiar los cambios pendientes de este módulo
        // y recargar las puertas desde la base de datos para reflejar los cambios
        if (!hasError) {
          setPendingDoorOrderChanges(prev => {
            const updated = { ...prev };
            delete updated[moduleId];
            return updated;
          });
          
          // Recargar las puertas desde la base de datos para reflejar los cambios
          if (externalId) {
            setIsLoadingDoor(true);
            try {
              await handleGetDoors();
            } catch (error) {
              console.error('Error recargando puertas después de actualizar orden:', error);
            } finally {
              setIsLoadingDoor(false);
            }
          }
        }

        return { success: !hasError };
      };
    
    useEffect(() => {
        if (externalId) {
            handleGetInfoPlatform(externalId);
        }else{
            const url = new URL(window.location.href);
            url.searchParams.set('view', 'create');
            url.searchParams.set('opt', 'general');
            window.history.pushState({}, '', url); 
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [externalId]); 
    
    
      useEffect(() => {
          getProvidersPlatforms();
      // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

    // En pantallas pequeñas (tablet/móvil), hacer scroll hacia la sección de info de la puerta al estar en Doors o al cambiar de puerta seleccionada
    useEffect(() => {
      if (optionSelect !== 'doors') return;
      const isSmallViewport = typeof window !== 'undefined' && window.innerWidth <= 1024;
      if (!isSmallViewport || !leftColumnRef.current) return;
      const timer = setTimeout(() => {
        leftColumnRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      }, 100);
      return () => clearTimeout(timer);
    }, [optionSelect, selectDoor]);

  const activeTabIndex = CONFIG_KEYS.indexOf(optionSelect);
  const setActiveTabIndex = (idx) => {
    const key = CONFIG_KEYS[idx];
    if (key) handleOptionChange(key);
  };

  const tabItems = useMemo(() => CONFIG_KEYS.map((key) => ({
    label: configSelector[key].title,
    icon: configSelector[key].icon,
    component: configSelector[key].components({
      optionSelect,
      configLocker,
      setOptionSelect: handleOptionChange,
      externalId,
      dataDetails: platformDetails,
      setDataDetails: setPlatformDetails,
      setExternalId,
      isLoadingData: isLoading,
      setConfigLocker,
      settingsLocker,
      setSettingsLocker,
      handleGetInfoPlatform,
      selectDoor,
      setSelectDoor,
      doorsInBd,
      setDoorsInBd,
      isLoadingDoor,
      setIsLoadingDoor,
      groupDoors,
      handleGetDoors,
      handleGetModules,
      typeView,
      optionsProviders,
      setEditModeModule,
      editModeModule,
      setEditModeDoors,
      editModeDoors,
      refreshAssignments,
      setRefreshAssignments,
      pendingDoorOrderChanges,
      applyPendingDoorOrderChanges,
      discardPendingDoorOrderChanges,
      onDoorOrdersChange: handleDoorOrdersChange,
      reloadDoors,
      permissions,
    }),
    show: true,
    disabled: key !== 'general' && !externalId,
  })), [optionSelect, configLocker, externalId, platformDetails, isLoading, settingsLocker, selectDoor, doorsInBd, isLoadingDoor, typeView, optionsProviders, editModeModule, editModeDoors, refreshAssignments, pendingDoorOrderChanges, permissions]);

  return (
    <>
      <div className={styles.settingsGrid} style={{ marginTop: '24px' }}>
        <div className={styles.leftColumn} ref={leftColumnRef}>
          <ContentCard padding="md">
            <Tabs
              items={tabItems}
              activeTab={activeTabIndex >= 0 ? activeTabIndex : 0}
              setActiveTab={setActiveTabIndex}
              lazy={true}
            />
          </ContentCard>
        </div>

        <div className={styles.rightColumn}>
          <ContentCard
            title={
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                <MdOutlineViewInAr size={20} style={{ marginRight: '0.25rem' }} />
                {translate("global.preview")}
              </span>
            }
            padding="none"
          >
            <PreviewLocker
              data={platformDetails}
              externalId={externalId}
              config={configLocker}
              setConfig={setConfigLocker}
              setSelectDoor={setSelectDoor}
              selectDoor={selectDoor}
              doorsInBd={doorsInBd}
              setOptionSelect={setOptionSelect}
              isLoadingDoor={isLoadingDoor}
              isLoadingData={isLoading}
              setModulesIsCompleted={setModulesIsCompleted}
              editModeModule={editModeModule}
              editModeDoors={editModeDoors}
              refreshAssignments={refreshAssignments}
              onDoorOrdersChange={handleDoorOrdersChange}
              canReorderDoors={!!permissions?.canReorderDoors}
              pendingDoorOrderChanges={pendingDoorOrderChanges}
            />
          </ContentCard>
        </div>
      </div>

      <ModalContent
        title={modal.title}
        body={modal.body}
        footer={modal.footer}
        isOpenModal={showModal}
        onHide={() => {
          setShowModal(false);
          window.location.href = '/dashboard/platforms/general';
        }}
      />
    </>
  )
}

export default GroupFormCreate